import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Layers, Loader2, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl, gramas } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, ErrorState, Field, Loading, PageHeader, Surface, Thumb } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { FilamentPicker } from "@/components/ds/FilamentPicker";
import { NativeSelect } from "@/components/ds/NativeSelect";

/** Busca sem acento e sem diferenciar maiúsculas. */
const norm = (v) =>
  (v || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/** 270 -> "4h30" · 45 -> "45min" */
const formatTime = (min) => {
  const total = Math.round(Number(min) || 0);
  if (!total) return "—";
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m}min`;
  return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

/** 150 -> "2:30" */
const minutesToHm = (min) => {
  const total = Math.round(Number(min) || 0);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/** Digita só números e o ":" entra sozinho: "230" -> "2:30" · "1230" -> "12:30" */
const maskHm = (value) => {
  const digits = String(value).replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, digits.length - 2)}:${digits.slice(-2)}`;
};

/** "2:30" -> 150 */
const hmToMinutes = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length <= 2) return Number(digits) * 60;
  return Number(digits.slice(0, digits.length - 2)) * 60 + Number(digits.slice(-2));
};

function PlatesInner({ initialProductId, autoNew }) {
  const [plates, setPlates] = useState(null);
  const [filaments, setFilaments] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // objeto plate ou "new"
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState("");

  const term = norm(search);
  const visiblePlates = !plates
    ? []
    : term
    ? plates.filter((p) =>
        [p.name, p.product_name].filter(Boolean).some((f) => norm(f).includes(term))
      )
    : plates;

  const load = useCallback(async () => {
    setError("");
    try {
      const [p, f, pr] = await Promise.all([
        api.get("/plates"),
        api.get("/filaments"),
        api.get("/products"),
      ]);
      setPlates(p.data);
      setFilaments(f.data);
      setProducts(pr.data);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (autoNew) setEditing("new");
  }, [autoNew]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/plates/${toDelete.id}`);
      toast.success("Plate excluída.");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setToDelete(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="plates-page">
      <PageHeader
        title="Plates"
        subtitle="Uma Plate é a receita da sua produção: tipo, quantidade produzida, tempo e filamentos. Um produto pode ter várias Plates. Cadastrar não retira nada do estoque."
        action={
          <Button
            onClick={() => setEditing("new")}
            data-testid="new-plate-btn"
            className="h-11 gap-2 rounded-full px-5 font-semibold transition-transform active:scale-95"
          >
            <Plus className="h-4 w-4" /> Nova Plate
          </Button>
        }
      />

      {plates === null && !error && <Loading label="Carregando suas Plates..." />}
      {error && <ErrorState message={error} onRetry={load} />}

      {plates && plates.length === 0 && (
        <EmptyState
          icon={Layers}
          title="Nenhuma Plate cadastrada"
          description="Crie sua primeira receita de produção escolhendo um filamento do estoque."
          action={
            <Button onClick={() => setEditing("new")} data-testid="empty-new-plate-btn" className="gap-2 rounded-full">
              <Plus className="h-4 w-4" /> Nova Plate
            </Button>
          }
        />
      )}

      {plates && plates.length > 0 && (
        <>
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar Plate..."
              data-testid="plates-search-input"
              className="h-10 rounded-full pl-9"
            />
          </div>
          {visiblePlates.length === 0 && (
            <EmptyState
              icon={Search}
              title="Nenhum item encontrado."
              description="Tente outro nome de Plate ou de produto."
            />
          )}
        </>
      )}

      {plates && visiblePlates.length > 0 && (
        <div className="grid gap-4 stagger md:grid-cols-2 xl:grid-cols-3">
          {visiblePlates.map((p) => (
            <Surface key={p.id} className="p-5" data-testid={`plate-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-display text-base font-medium" data-testid={`plate-name-${p.id}`}>
                    {p.name}
                  </p>
                  {p.product_name && (
                    <p className="truncate text-xs text-muted-foreground">Produto: {p.product_name}</p>
                  )}
                  <span
                    className={`mt-1.5 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                      p.plate_type === "COMPONENTE"
                        ? "border-sky-500/40 bg-sky-500/15 text-sky-300"
                        : "border-brand/40 bg-brand/15 text-brand"
                    }`}
                    data-testid={`plate-type-badge-${p.id}`}
                  >
                    {p.plate_type === "COMPONENTE" ? "Peça / componente" : "Produto completo"}
                  </span>
                  {p.fixed_filaments && (
                    <span
                      className="rounded-full border border-white/15 bg-secondary/60 px-2 py-0.5 text-[10px] text-muted-foreground"
                      data-testid={`plate-fixed-badge-${p.id}`}
                    >
                      Filamentos fixos
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(p)}
                    data-testid={`plate-edit-btn-${p.id}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setToDelete(p)}
                    data-testid={`plate-delete-btn-${p.id}`}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              <ul className="mt-4 space-y-2">
                {p.filaments.map((it) => (
                  <li
                    key={it.filament_id}
                    className="flex items-center gap-3 rounded-md border border-white/10 bg-secondary/40 p-2.5"
                  >
                    <Thumb src={fileUrl(it.filament_photo)} alt={it.filament_label} className="h-9 w-9" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{it.filament_label}</p>
                      <p className="truncate text-xs text-muted-foreground">{it.filament_brand}</p>
                    </div>
                    <span className="font-num text-sm">{gramas(it.quantity_g)}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/10 pt-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Produz por execução</p>
                  <p className="font-num text-base" data-testid={`plate-output-${p.id}`}>
                    {p.output_quantity} un
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tempo de produção</p>
                  <p className="font-num text-base" data-testid={`plate-time-${p.id}`}>
                    {p.print_time_min ? formatTime(p.print_time_min) : "—"}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3 text-sm">
                <span className="text-muted-foreground">Consumo por impressão</span>
                <span className="font-num font-medium text-brand" data-testid={`plate-total-${p.id}`}>
                  {gramas(p.total_g)}
                </span>
              </div>
            </Surface>
          ))}
        </div>
      )}

      {editing && (
        <PlateModal
          plate={editing === "new" ? null : editing}
          defaultProductId={initialProductId}
          filaments={filaments}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-plate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Plate?</AlertDialogTitle>
            <AlertDialogDescription>
              A Plate <strong>{toDelete?.name}</strong> será removida. Seu estoque não será alterado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-plate-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="confirm-delete-plate-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Plates() {
  const [params] = useSearchParams();
  return (
    <PlatesInner
      key={params.toString()}
      initialProductId={params.get("product") || ""}
      autoNew={params.get("new") === "1"}
    />
  );
}

export function PlateModal({ plate, filaments, products, defaultProductId, requireProduct = true, onClose, onSaved }) {
  const [name, setName] = useState(plate?.name || "");
  const [plateType, setPlateType] = useState(plate?.plate_type || "COMPLETO");
  const [outputQuantity, setOutputQuantity] = useState(String(plate?.output_quantity ?? 1));
  const [productId, setProductId] = useState(plate?.product_id || defaultProductId || "");
  const [printTime, setPrintTime] = useState(
    plate?.print_time_min ? minutesToHm(plate.print_time_min) : ""
  );
  const [notes, setNotes] = useState(plate?.notes || "");
  const [items, setItems] = useState(
    (plate?.filaments || []).map((f) => ({ ...f, quantity_g: String(f.quantity_g) }))
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fixedFilaments, setFixedFilaments] = useState(!!plate?.fixed_filaments);
  const [busy, setBusy] = useState(false);

  const addFilament = (f) => {
    if (items.some((i) => i.filament_id === f.id)) {
      toast.error("Este filamento já está na Plate.");
      return;
    }
    if (items.length >= 4) {
      toast.error("Uma Plate aceita no máximo 4 filamentos.");
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        filament_id: f.id,
        quantity_g: "",
        filament_label: `${f.material} ${f.color_name}`,
        filament_brand: f.brand,
        filament_photo: f.photo_path,
      },
    ]);
    setPickerOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Selecione pelo menos um filamento.");
      return;
    }
    if (items.some((i) => !Number(i.quantity_g) || Number(i.quantity_g) <= 0)) {
      toast.error("Informe o peso utilizado de cada filamento.");
      return;
    }
    if (!Number(outputQuantity) || Number(outputQuantity) < 1) {
      toast.error("Informe quantas unidades esta Plate produz por execução.");
      return;
    }
    if (requireProduct && !productId) {
      toast.error("Escolha o produto desta Plate.");
      return;
    }
    setBusy(true);
    const body = {
      name,
      plate_type: plateType,      output_quantity: Number(outputQuantity || 1),
      product_id: productId || null,
      print_time_min: hmToMinutes(printTime),
      notes,
      active: true,
      fixed_filaments: fixedFilaments,
      filaments: items.map((i) => ({ filament_id: i.filament_id, quantity_g: Number(i.quantity_g) })),
    };
    try {
      if (plate) await api.put(`/plates/${plate.id}`, body);
      else await api.post("/plates", body);
      toast.success(plate ? "Plate atualizada com sucesso." : "Plate cadastrada com sucesso.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const total = items.reduce((s, i) => s + Number(i.quantity_g || 0), 0);

  return (
    <>
      <ResponsiveModal
        open
        onOpenChange={(o) => !o && onClose()}
        title={plate ? "Editar Plate" : "Nova Plate"}
        description="Escolha um filamento que já existe no seu estoque e informe o peso necessário. Nada é retirado do estoque agora."
        testid="plate-modal"
      >
        <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
          <Field label="Nome da Plate">
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ex.: Cuia Premium" data-testid="plate-name-input" />
          </Field>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Tipo da Plate
            </span>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                {
                  value: "COMPLETO",
                  title: "Produto completo",
                  hint: "Esta Plate produz o produto final.",
                },
                {
                  value: "COMPONENTE",
                  title: "Peça / componente",
                  hint: "Esta Plate produz uma parte do produto.",
                },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPlateType(opt.value)}
                  data-testid={`plate-type-${opt.value.toLowerCase()}`}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    plateType === opt.value
                      ? "border-primary bg-primary/10"
                      : "border-white/10 bg-secondary/40 hover:border-primary/40"
                  }`}
                >
                  <p className="text-sm font-medium">{opt.title}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{opt.hint}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Quantidade produzida" hint="Unidades por execução da Plate">
              <Input
                type="number"
                min="1"
                step="1"
                value={outputQuantity}
                onChange={(e) => setOutputQuantity(e.target.value)}
                className="font-num"
                data-testid="plate-output-quantity-input"
              />
            </Field>
            <Field label="Tempo de produção (h:min)" hint={formatTime(hmToMinutes(printTime) || 0)}>
              <Input
                inputMode="numeric"
                placeholder="2:30"
                value={printTime}
                onChange={(e) => setPrintTime(maskHm(e.target.value))}
                className="font-num"
                data-testid="plate-time-input"
              />
            </Field>
          </div>

          <Field label="Produto" hint="Toda Plate pertence a um produto. Um produto pode ter várias Plates.">
            <NativeSelect
              value={productId}
              onChange={setProductId}
              data-testid="plate-product-select"
              placeholder="Escolha o produto"
              options={products.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Field>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Filamentos utilizados
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen(true)}
                disabled={items.length >= 4}
                data-testid="select-filament-btn"
                className="h-8 gap-1.5 rounded-full text-xs"
              >
                <Plus className="h-3.5 w-3.5" /> Selecionar Filamento
              </Button>
            </div>

            {items.length === 0 ? (
              <p className="rounded-lg border border-dashed border-white/15 px-4 py-6 text-center text-sm text-muted-foreground" data-testid="plate-items-empty">
                Nenhum filamento selecionado.
              </p>
            ) : (
              <ul className="space-y-2">
                {items.map((it, idx) => (
                  <li
                    key={it.filament_id}
                    className="flex items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-2.5"
                    data-testid={`plate-item-${it.filament_id}`}
                  >
                    <Thumb src={fileUrl(it.filament_photo)} alt={it.filament_label} className="h-9 w-9" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{it.filament_label}</p>
                      <p className="truncate text-xs text-muted-foreground">{it.filament_brand}</p>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={it.quantity_g}
                      onChange={(e) =>
                        setItems((prev) =>
                          prev.map((p, i) => (i === idx ? { ...p, quantity_g: e.target.value } : p))
                        )
                      }
                      placeholder="g"
                      className="h-9 w-20 font-num"
                      data-testid={`plate-item-qty-${it.filament_id}`}
                    />
                    <button
                      type="button"
                      onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                      data-testid={`plate-item-remove-${it.filament_id}`}
                      className="text-muted-foreground transition-colors hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Field label="Observações">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="plate-notes-input" />
          </Field>

          <div
            className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-secondary/40 px-4 py-3"
            data-testid="plate-fixed-filaments-row"
          >
            <div className="min-w-0">
              <p className="text-sm">Filamentos fixos</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Quando ativado, esta Plate sempre usa os filamentos cadastrados nela e ignora o
                remapeamento de cores da combinação.
              </p>
            </div>
            <Switch
              checked={fixedFilaments}
              onCheckedChange={setFixedFilaments}
              data-testid="plate-fixed-filaments-switch"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-brand/30 bg-brand/10 px-4 py-3 text-sm">
            <span className="text-muted-foreground">Consumo total da Plate</span>
            <span className="font-num text-lg text-brand" data-testid="plate-total-preview">
              {gramas(total)}
            </span>
          </div>

          <Button type="submit" disabled={busy} data-testid="save-plate-btn" className="h-11 rounded-full font-semibold transition-transform active:scale-95">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
          </Button>
        </form>
      </ResponsiveModal>

      <ResponsiveModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title="Selecionar Filamento"
        description="Somente filamentos já cadastrados no seu estoque."
        testid="plate-filament-picker-modal"
      >
        <div className="mt-2">
          {filaments.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              Você ainda não tem filamentos. Cadastre uma cor no Estoque primeiro.
            </p>
          ) : (
            <FilamentPicker filaments={filaments} onSelect={addFilament} testid="plate-filament-picker" />
          )}
        </div>
      </ResponsiveModal>
    </>
  );
}
